<script setup lang="ts">
const { t } = useI18n();

useHead({
  titleTemplate: () => t("mainNav.rates.title") + " | SMSBAT",
  meta: [
    { name: "description", content: () => t("seo.description") },
    { name: "keywords", content: "Viber Business Pricing, SMS Pricing, Rates, OTP Pricing, Viber API Pricing" },
    { property: "og:title", content: () => t("mainNav.rates.title") + " | SMSBAT" },
    { property: "og:description", content: () => t("seo.description") },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "SMSBAT" },
  ]
});

// Tab definitions dynamically picking up translations
const tabs = computed(() => [
  { id: 'business-messages', label: t('mainNav.viber.bm'), component: 'BMDefaultRatesTable' },
  { id: 'chat-sessions', label: t('mainNav.viber.chatSessions'), component: 'BMChatSessionsRatesTable' },
  { id: 'chatbots', label: t('mainNav.viber.bots'), component: 'BotsDefaultRatesTable' },
  { id: 'business-calls', label: t('mainNav.viber.bc'), component: 'BCDefaultRatesTable' },
  { id: 'otp', label: t('mainNav.viber.viberOtp'), component: 'ViberOtpRatesTable' },
  { id: 'carousel', label: t('mainNav.rates.viberCarousel'), component: 'ViberCarouselRatesTable' },
  { id: 'survey', label: t('mainNav.rates.viberSurvey'), component: 'ViberSurveyRatesTable' }
]);

const activeTab = ref('business-messages');
</script>

<template>
  <div class="pricing-general-page bg-gray-50 min-h-screen pb-20">
    <!-- Hero Section -->
    <div class="pricing-banner relative" style="background: linear-gradient(135deg, rgba(48, 112, 182, 0.05) 0%, rgba(48, 112, 182, 0.15) 100%); padding: 120px 0 80px 0;">
      <div class="pricing-banner-content container mx-auto px-4 text-center flex flex-col items-center justify-center">
        <h1 class="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 leading-tight">
          {{ $t('mainNav.rates.title') }}
        </h1>
        <p class="text-lg md:text-xl text-gray-600 max-w-2xl">
          {{ $t('seo.description') }}
        </p>
      </div>
    </div>

    <!-- Custom Tab Navigation -->
    <div class="container mx-auto px-4 max-w-6xl mt-4 mb-4 relative z-10">
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 overflow-x-auto custom-scrollbar">
        <div class="flex space-x-2 min-w-max p-1">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="px-6 py-4 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap outline-none"
            :class="[
              activeTab === tab.id
                ? 'bg-[#3070b6] text-white shadow-md transform scale-105 z-10'
                : 'text-gray-600 hover:bg-blue-50 hover:text-blue-700'
            ]"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Dynamic Content Area -->
    <div class="container mx-auto px-4 max-w-7xl mt-12">
      <div class="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-10 overflow-hidden min-h-[500px]">
        
        <transition name="fade" mode="out-in">
          <!-- Business Messages -->
          <div v-if="activeTab === 'business-messages'" key="business-messages" class="tab-content w-full overflow-x-auto">
            <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.viber.bm') }} {{ $t('global.price') }}</h2>
            <BMDefaultRatesTable />
          </div>

          <!-- Chat Sessions -->
          <div v-else-if="activeTab === 'chat-sessions'" key="chat-sessions" class="tab-content w-full overflow-x-auto">
            <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.viber.chatSessions') }} {{ $t('global.price') }}</h2>
            <BMChatSessionsRatesTable />
          </div>

          <!-- Chatbots -->
          <div v-else-if="activeTab === 'chatbots'" key="chatbots" class="tab-content w-full overflow-x-auto">
             <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.viber.bots') }} {{ $t('global.price') }}</h2>
            <ClientOnly>
              <LazyBotsDefaultRatesTable />
            </ClientOnly>
          </div>

          <!-- Business Calls -->
          <div v-else-if="activeTab === 'business-calls'" key="business-calls" class="tab-content w-full overflow-x-auto">
             <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.viber.bc') }} {{ $t('global.price') }}</h2>
            <ClientOnly>
              <LazyBCDefaultRatesTable />
            </ClientOnly>
          </div>

          <!-- OTP -->
          <div v-else-if="activeTab === 'otp'" key="otp" class="tab-content w-full overflow-x-auto">
             <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.viber.viberOtp') }} {{ $t('global.price') }}</h2>
            <ClientOnly>
              <LazyViberOtpRatesTable />
            </ClientOnly>
          </div>

          <!-- Carousel -->
          <div v-else-if="activeTab === 'carousel'" key="carousel" class="tab-content w-full overflow-x-auto">
             <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.rates.viberCarousel') }} {{ $t('global.price') }}</h2>
            <ClientOnly>
              <LazyViberCarouselRatesTable />
            </ClientOnly>
          </div>

          <!-- Survey -->
          <div v-else-if="activeTab === 'survey'" key="survey" class="tab-content w-full overflow-x-auto">
             <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center lg:text-left">{{ $t('mainNav.rates.viberSurvey') }} {{ $t('global.price') }}</h2>
            <ClientOnly>
              <LazyViberSurveyRatesTable />
            </ClientOnly>
          </div>
        </transition>

      </div>
    </div>
    
  </div>
</template>

<style scoped lang="scss">
/* Smooth Vue Transition for Tabs */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Custom Scrollbar for mobile tab swiping */
.custom-scrollbar::-webkit-scrollbar {
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

/* Ensure wide tables don't break layout */
.tab-content {
  min-height: 450px;
}
</style>
